import { defaultText } from '@phaser/helpers/Text';
import { sendMessage } from '@api/websocket';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';

interface ActiveItem {
  key: string;
  endsAt: number;
  tween?: Phaser.Tweens.Tween;
  container?: Phaser.GameObjects.Container;
};

interface UtilityItem {
  key: string;
  amount: number;
};

const ITEM_SIZE = 42;
const ITEM_DURATION = 240_000;
const PASSIVE_GEAR = ['fishingSpear', 'miningLaser'];

export class UtilityGear {
  private _equip?: string;
  private _scene = Game.current;
  private _items: UtilityItem[] = [];
  private _activeItems: ActiveItem[] = [];

  private _equipContainer?: Phaser.GameObjects.Container;
  private _utilityContainer?: Phaser.GameObjects.Container;

  constructor(type: 'fishing' | 'mining') {
    const userData = useAppStore.getState().userData;
    this._items = userData.items
      .filter(item => item.key.startsWith(type));
    if (!this._items.length) return;

    this._equip = this._items
      .filter(i => PASSIVE_GEAR.some(l => i.key.startsWith(l)))
      .map(i => i.key).sort().pop();

    this._placeEquip();
    this._redraw();
  }

  public getDifficultyMultiplier(): number {
    if (!this._equip) return 1;
    if (this._equip.endsWith('2')) return 1.5;
    if (this._equip.endsWith('1')) return 1.35;
    return 1;
  }

  public getActiveItems(): string[] {
    return this._activeItems.map(i => i.key);
  }

  public destroy() {
    this._equipContainer?.destroy();
    this._utilityContainer?.destroy();

    this._activeItems.forEach(i => i.container?.destroy());
    this._activeItems.forEach(i => i.tween?.stop());

    this._equipContainer = undefined;
    this._utilityContainer = undefined;
    this._activeItems = [];
  }

  private _placeEquip() {
    if (!this._equip) return;

    this._equipContainer = this._scene.add.container(6, 6)
      .setDepth(DEPTH.MINIGAME + 10);

    const background = this._scene.add
      .rectangle(0, 0, ITEM_SIZE, ITEM_SIZE, 0x252525, 1)
      .setStrokeStyle(1, 0x413F3D)
      .setOrigin(0);
    this._equipContainer.add(background);

    const image = this._scene.add.image(3, 3, 'sprites', this._equip).setOrigin(0);
    image.setScale(Math.max((ITEM_SIZE - 6) / image.width, (ITEM_SIZE - 6) / image.height));
    this._equipContainer.add(image);
  }

  private _redraw() {
    this._drawUtility();
    this._drawActive();
  }

  private _drawUtility() {
    const camera = this._scene.cameras.main;
    const height = camera.displayHeight;
    const width = camera.displayWidth;

    if (this._utilityContainer) this._utilityContainer.destroy();

    this._utilityContainer = this._scene.add.container(0, height - ITEM_SIZE - 42)
      .setDepth(DEPTH.MINIGAME + 10);

    const items = this._items
      .filter(i => !PASSIVE_GEAR.some(l => i.key.startsWith(l)))
      .filter(i => i.amount > 0);

    const yOffset = width / 2 - items.length * ITEM_SIZE / 2;

    items.forEach((item, index) => {
      const x = yOffset + index * ITEM_SIZE;

      const background = this._scene.add
        .rectangle(x, 0, ITEM_SIZE, ITEM_SIZE, 0x252525, 0.5)
        .setStrokeStyle(2, 0xDEF141)
        .setOrigin(0)
        .setInteractive();
      this._utilityContainer?.add(background);

      background.on('pointerdown', () => {
        this._activateItem(item.key);
      });

      const image = this._scene.add.image(x + 3, 3, 'sprites', item.key).setOrigin(0);
      image.setScale(Math.max((ITEM_SIZE - 6) / image.width, (ITEM_SIZE - 6) / image.height));
      this._utilityContainer?.add(image);

      const amount = this._scene.add.text(x + 2, 0, item.amount.toString(), {
        ...defaultText,
        fontSize: 12,
      });
      this._utilityContainer?.add(amount);
    });
  }

  private _drawActive() {
    this._activeItems.forEach((item, index) => {
      const offset = (index + 1) * (ITEM_SIZE + 6) + 6;

      if (item.container) item.container.destroy();
      if (item.tween) item.tween.stop();

      const progressHeight = ITEM_SIZE * ((item.endsAt - Date.now()) / ITEM_DURATION);

      item.container = this._scene.add.container(offset, 6)
        .setDepth(DEPTH.MINIGAME + 10);

      const background = this._scene.add
        .rectangle(0, 0, ITEM_SIZE, ITEM_SIZE, 0x252525, 0.5)
        .setStrokeStyle(2, 0xDEF141)
        .setOrigin(0);
      item.container.add(background);

      const activeBackground = this._scene.add
        .rectangle(0, 0, ITEM_SIZE, progressHeight, 0xDEF141, 0.5)
        .setOrigin(0);
      item.container.add(activeBackground);
      
      const image = this._scene.add.image(3, 3, 'sprites', item.key).setOrigin(0);
      image.setScale(Math.max((ITEM_SIZE - 6) / image.width, (ITEM_SIZE - 6) / image.height));
      item.container.add(image);

      item.tween = this._scene.tweens.add({
        targets: activeBackground,
        height: 0,
        duration: item.endsAt - Date.now(),
        onComplete: () => {
          item.container?.destroy();
          item.tween?.stop();

          this._activeItems = this._activeItems.filter(i => i.key !== item.key);
          this._redraw();
        },
      });
    });
  }

  private _activateItem(key: string) {
    if (this._activeItems.some(i => i.key === key)) return;

    this._items.find(i => i.key === key)!.amount--;
    this._items = this._items.filter(i => i.amount > 0);

    const itemCode = Shared.itemData.find(i => i.key === key)?.id;
    if (!itemCode) return;

    sendMessage(new Shared.UseItemMessage({ itemCode }));

    this._activeItems.push({ key, endsAt: Date.now() + ITEM_DURATION });
    this._redraw();
  }
}